import type { Locale } from 'date-fns';
import { formatISO, isAfter, parseISO } from 'date-fns';

/**
 * Maximum selectable year for ion-datetime components (1 years in advance)
 * Format: YYYY
 */
export const MAX_SELECTABLE_YEAR_FOR_ION_DATETIME =
  new Date().getFullYear() + 1;

/**
 * Which day should be first in calendar module. 1 means Monday.
 * It is preferable default value to be used across the app.
 */
export const DEFAULT_FIRST_DAY_IN_CALENDAR_COLUMN = 1;

/**
 * @param {number} hours - Time of the day in hours (0 - 24)
 *
 * @example
 * formatHoursISO(14); // "14:00:00Z"
 */
export function formatHoursISO(hours: number) {
  return formatISO(new Date().setHours(hours, 0, 0, 0), {
    representation: 'time',
  });
}

/**
 * @param {string} ISOTime - Time of the day in ISO time format
 *
 * @example
 * parseHoursISO("14:00:00Z"); // 14
 */
export function parseHoursISO(ISOTime: string) {
  const ISODate = formatISO(new Date(), { representation: 'date' });
  return parseISO(`${ISODate}T${ISOTime}`).getHours();
}

/**
 * @param {string} ISOTime - Time of the day in ISO time format
 *
 * @example
 * formatDateISO("14:00:00Z"); // 2022-11-18T14:00:00.000Z
 */

export function formatDateISO(ISOTime: string) {
  const date = formatISO(new Date(), { representation: 'date' });
  return parseISO(`${date}T${ISOTime}`).toISOString();
}

export function generateMonthsForYear(year: number) {
  const months: Interval[] = [];
  const date = new Date(year, 0, 1);
  for (let i = 0; i < 12; i++) {
    const start = new Date(year, date.getMonth() + i, 1);
    const end = new Date(year, date.getMonth() + i + 1, 0);

    if (isAfter(new Date(), end))
      months.push({
        start,
        end,
      });
    else break;
  }

  return months;
}

/**
 * Get a locale-specific date format pattern from a date-fns Locale object.
 *
 * @param locale - date-fns Locale object (from DateFnsConfigurationService)
 * @param options - Format options
 * @param options.noYear - Remove year component from format (default: false)
 * @returns Date format string (e.g., 'dd.MM.yyyy' for German, 'dd.MM' without year)
 *
 * @example
 * ```typescript
 * const locale = dateConfig.locale();
 * getLocaleDateFormat(locale); // 'dd.MM.yyyy' for German locale
 * getLocaleDateFormat(locale, { noYear: true }); // 'dd.MM' for German locale
 * ```
 */
export function getLocaleDateFormat(
  locale: Locale,
  options: { noYear?: boolean } = {},
): string {
  const shortDateFormat = locale.formatLong?.date({ width: 'short' }) ?? 'P';

  if (options.noYear) {
    // Remove year component (y, yy, yyyy, etc.) from the format
    return shortDateFormat.replace(/[/.\-\s]*y+[/.\-\s]*/gi, '').trim();
  }

  return shortDateFormat;
}

/**
 * Get a locale-specific time format pattern from a date-fns Locale object.
 * Defaults to 24-hour format unless useAmPm is specified.
 *
 * @param locale - date-fns Locale object (from DateFnsConfigurationService)
 * @param options - Format options
 * @param options.useAmPm - Use 12-hour format with AM/PM (default: false, uses 24-hour)
 * @returns Time format string (e.g., 'HH:mm' for 24-hour, 'h:mm a' for 12-hour)
 *
 * @example
 * ```typescript
 * const locale = dateConfig.locale();
 * getLocaleTimeFormat(locale); // 'HH:mm' (24-hour, default)
 * getLocaleTimeFormat(locale, { useAmPm: true }); // 'h:mm a' for US locale (12-hour)
 * ```
 */
export function getLocaleTimeFormat(
  locale: Locale,
  options: { useAmPm?: boolean } = {},
): string {
  const timeFormat = locale.formatLong?.time({ width: 'short' }) ?? 'p';

  if (!options.useAmPm) {
    // Force 24-hour format: convert h/hh to HH and remove AM/PM markers
    return timeFormat
      .replace(/\bh{1,2}\b/g, 'HH')
      .replace(/\s*a+\s*/gi, '')
      .trim();
  }

  return timeFormat;
}

/**
 * Convert a date/time format pattern to a placeholder string.
 * Replaces format tokens with dashes.
 *
 * @param formatPattern - A date-fns format pattern (e.g., 'dd.MM', 'HH:mm', 'M/d')
 * @returns Placeholder string (e.g., '--.--', '--:--', '--/--')
 *
 * @example
 * ```typescript
 * formatPatternToPlaceholder('dd.MM'); // '--.--'
 * formatPatternToPlaceholder('M/d');   // '--/--'
 * formatPatternToPlaceholder('HH:mm'); // '--:--'
 * ```
 */
export function formatPatternToPlaceholder(formatPattern: string): string {
  return formatPattern.replace(/[a-zA-Z]+/g, '--').replace(/\s+/g, ' ');
}

/**
 * Get a locale-specific date placeholder string.
 *
 * @param locale - date-fns Locale object (from DateFnsConfigurationService)
 * @param options - Format options
 * @param options.noYear - Remove year component from placeholder (default: false)
 * @returns Date placeholder (e.g., '--.--.--.--' for German, '--.--' without year)
 *
 * @example
 * ```typescript
 * const locale = dateConfig.locale();
 * getLocaleDatePlaceholder(locale); // '--.--.--.--' for German
 * getLocaleDatePlaceholder(locale, { noYear: true }); // '--.--' for German
 * ```
 */
export function getLocaleDatePlaceholder(
  locale: Locale,
  options: { noYear?: boolean } = {},
): string {
  const dateFormat = getLocaleDateFormat(locale, options);
  return formatPatternToPlaceholder(dateFormat);
}

/**
 * Get a locale-specific time placeholder string.
 *
 * @param locale - date-fns Locale object (from DateFnsConfigurationService)
 * @param options - Format options
 * @param options.useAmPm - Use 12-hour format placeholder with AM/PM (default: false)
 * @returns Time placeholder (e.g., '--:--' for 24-hour, '--:-- --' for 12-hour with AM/PM)
 *
 * @example
 * ```typescript
 * const locale = dateConfig.locale();
 * getLocaleTimePlaceholder(locale); // '--:--' (24-hour, default)
 * getLocaleTimePlaceholder(locale, { useAmPm: true }); // '--:-- --' (12-hour with AM/PM)
 * ```
 */
export function getLocaleTimePlaceholder(
  locale: Locale,
  options: { useAmPm?: boolean } = {},
): string {
  const timeFormat = getLocaleTimeFormat(locale, options);
  return formatPatternToPlaceholder(timeFormat);
}
